import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showApiErrorToast, type EncounterPlace } from '@utils'
import { useLanguage } from '@context'

interface UpdateProfileEncounterPlaceData {
  profileId: string
  encounterPlace: EncounterPlace | null
}

type PreviousEntries = [readonly unknown[], unknown][]

const updateProfileEncounterPlace = async ({
  profileId,
  encounterPlace,
}: UpdateProfileEncounterPlaceData): Promise<void> => {
  await api.patch(`/profile/${profileId}/encounter-place`, {
    encounterPlace: encounterPlace ?? undefined,
  })
}

export const useUpdateProfileEncounterPlace = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<
    void,
    Error,
    UpdateProfileEncounterPlaceData,
    { previousEntries: PreviousEntries }
  >({
    mutationFn: updateProfileEncounterPlace,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['profile', variables.profileId] })
      const previousEntries = queryClient.getQueriesData({
        queryKey: ['profile', variables.profileId],
      }) as PreviousEntries
      queryClient.setQueriesData({ queryKey: ['profile', variables.profileId] }, (old: unknown) =>
        old && typeof old === 'object'
          ? { ...(old as object), encounterPlace: variables.encounterPlace ?? undefined }
          : old,
      )
      return { previousEntries }
    },
    onError: (err, _variables, context) => {
      if (context?.previousEntries) {
        for (const [key, data] of context.previousEntries) {
          queryClient.setQueryData(key, data)
        }
      }
      showApiErrorToast(t, err)
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: ['profile', variables.profileId] })
    },
  })
}
